import { SupportedLocaleEnum } from 'localization';
import {
  ProductItem,
  SellingPrice,
} from 'lane-shared/domains/billingPayments/types';
import {
  type SupportedPaymentCurrency,
  getCurrencyPrecision,
} from 'domains-billing-currency';
import { getPricing } from 'domains-billing-calculation';
import { PriceComponents } from '../../productCatalog/types';
import BigNumber from 'bignumber.js';

function hasSellingPrice(sellingPrice?: SellingPrice): boolean {
  return sellingPrice?.listPrice !== undefined;
}

function isFullSellingPrice(sellingPrice?: SellingPrice): boolean {
  return (
    sellingPrice?.listPrice !== undefined &&
    sellingPrice.grossTax !== undefined &&
    sellingPrice.netPrice !== undefined &&
    sellingPrice.grossMarkup !== undefined
  );
}

/**
 * Spreads a rounding delta across buckets so displayed subtotal / tax / admin
 * lines reconcile to an authoritative total (e.g. sum of `round(net×qty)`).
 * Prefer adjusting `tax`, then `adminFeeTax`, then `adminFee`, then `subTotal`.
 */
function distributeDraftTotalResidual(
  diff: BigNumber,
  buckets: {
    subTotal: BigNumber;
    tax: BigNumber;
    adminFee: BigNumber;
    adminFeeTax: BigNumber;
  },
  precision: number
): { subTotal: BigNumber; tax: BigNumber; adminFee: BigNumber; adminFeeTax: BigNumber } {
  if (diff.isZero()) {
    return buckets;
  }

  const order: (keyof typeof buckets)[] = [
    'tax',
    'adminFeeTax',
    'adminFee',
    'subTotal',
  ];

  let remaining = diff;
  const next = { ...buckets };

  for (const key of order) {
    if (remaining.isZero()) {
      break;
    }

    const adjusted = next[key].plus(remaining);

    if (!adjusted.isNegative() || key === 'subTotal') {
      next[key] = adjusted.decimalPlaces(precision);
      remaining = new BigNumber(0);
      break;
    }

    remaining = adjusted;
    next[key] = new BigNumber(0);
  }

  if (!remaining.isZero()) {
    next.subTotal = next.subTotal.plus(remaining).decimalPlaces(precision);
  }

  return next;
}

/**
 * Reconcile displayed buckets to `sum(round(netPrice × qty))` only when the admin-fee
 * charge-code path is active **and** at least one line has a **full** `sellingPrice`
 * snapshot (typical persisted charge) while **no** line uses a **partial** snapshot.
 * When admin-fee UI math is off, `netPrice` and bucket splits are not aligned the same way.
 */
function shouldReconcileTotalsToNetPerLine(
  listOfProducts: ProductItem[],
  isAdminFeeFeatureEnabled: boolean
): boolean {
  if (!isAdminFeeFeatureEnabled) {
    return false;
  }

  const hasPartialSellingPrice = listOfProducts.some(
    item =>
      hasSellingPrice(item.product.sellingPrice) &&
      !isFullSellingPrice(item.product.sellingPrice)
  );

  if (hasPartialSellingPrice) {
    return false;
  }

  return listOfProducts.some(item =>
    isFullSellingPrice(item.product.sellingPrice)
  );
}

export function getDraftTotalAmounts(
  listOfProducts: ProductItem[],
  currency: SupportedPaymentCurrency,
  locale: SupportedLocaleEnum,
  isAdminFeeFeatureEnabled = false
) {
  const precision = getCurrencyPrecision(currency);
  const reconcileToNet = shouldReconcileTotalsToNetPerLine(
    listOfProducts,
    isAdminFeeFeatureEnabled
  );

  let subTotal = new BigNumber(0);
  let tax = new BigNumber(0);
  let adminFee = new BigNumber(0);
  let adminFeeTax = new BigNumber(0);
  /** Sum of per-line `round(netPrice × qty)` when reconciling to persisted charge math. */
  let totalFromRoundedNetPerLine = new BigNumber(0);

  const roundExt = (value: BigNumber) =>
    reconcileToNet ? value : value.decimalPlaces(precision);

  listOfProducts.forEach(productItem => {
    const priceComponents: PriceComponents = {
      amount: productItem.product.amount,
      markup: productItem.product.markup,
      adminFee: productItem.product.adminFee,
      adminFeeTax: productItem.product.adminFeeTax,
      tax: productItem.product.tax,
      netPrice: productItem.product.netPrice,
      sellingPrice: productItem.product.sellingPrice,
    };

    const pricing = getPricing({
      currency,
      locale,
      priceComponents,
      isAdminFeeFeatureEnabled,
    });

    const quantity = new BigNumber(productItem.quantity);

    const adminFeeForProduct = new BigNumber(pricing.grossAdminFee);
    const adminFeeTaxForProduct = new BigNumber(pricing.grossAdminFeeTax);

    if (reconcileToNet) {
      totalFromRoundedNetPerLine = totalFromRoundedNetPerLine.plus(
        new BigNumber(pricing.netPrice)
          .multipliedBy(quantity)
          .decimalPlaces(precision)
      );
    }

    if (
      isAdminFeeFeatureEnabled &&
      hasSellingPrice(productItem.product.sellingPrice)
    ) {
      const sellingPrice = productItem.product.sellingPrice;
      const adminFeeInListPrice = isFullSellingPrice(sellingPrice)
        ? adminFeeForProduct
        : new BigNumber(0);

      subTotal = subTotal.plus(
        roundExt(
          new BigNumber(pricing.listPrice)
            .minus(adminFeeInListPrice)
            .multipliedBy(quantity)
        )
      );
      adminFee = adminFee.plus(
        roundExt(adminFeeForProduct.multipliedBy(quantity))
      );
      adminFeeTax = adminFeeTax.plus(
        roundExt(adminFeeTaxForProduct.multipliedBy(quantity))
      );
    } else if (
      isAdminFeeFeatureEnabled &&
      (adminFeeForProduct.gt(0) || adminFeeTaxForProduct.gt(0))
    ) {
      const listPriceWithoutAdminFee = new BigNumber(pricing.listPrice)
        .minus(adminFeeForProduct);

      subTotal = subTotal.plus(
        roundExt(listPriceWithoutAdminFee.multipliedBy(quantity))
      );
      adminFee = adminFee.plus(
        roundExt(adminFeeForProduct.multipliedBy(quantity))
      );
      adminFeeTax = adminFeeTax.plus(
        roundExt(adminFeeTaxForProduct.multipliedBy(quantity))
      );
    } else {
      subTotal = subTotal.plus(
        roundExt(new BigNumber(pricing.listPrice).multipliedBy(quantity))
      );
    }

    tax = tax.plus(roundExt(new BigNumber(pricing.grossTax).multipliedBy(quantity)));
  });

  if (!reconcileToNet) {
    const totalFromComponents = subTotal.plus(tax).plus(adminFee).plus(adminFeeTax);

    return {
      subTotal: subTotal.toNumber(),
      tax: tax.toNumber(),
      adminFee: adminFee.toNumber(),
      adminFeeTax: adminFeeTax.toNumber(),
      total: totalFromComponents.toNumber(),
    };
  }

  let roundedSubTotal = subTotal.decimalPlaces(precision);
  let roundedTax = tax.decimalPlaces(precision);
  let roundedAdminFee = adminFee.decimalPlaces(precision);
  let roundedAdminFeeTax = adminFeeTax.decimalPlaces(precision);

  const componentSum = roundedSubTotal
    .plus(roundedTax)
    .plus(roundedAdminFee)
    .plus(roundedAdminFeeTax);
  const diff = totalFromRoundedNetPerLine.minus(componentSum);

  if (!diff.isZero()) {
    const adjusted = distributeDraftTotalResidual(
      diff,
      {
        subTotal: roundedSubTotal,
        tax: roundedTax,
        adminFee: roundedAdminFee,
        adminFeeTax: roundedAdminFeeTax,
      },
      precision
    );
    roundedSubTotal = adjusted.subTotal;
    roundedTax = adjusted.tax;
    roundedAdminFee = adjusted.adminFee;
    roundedAdminFeeTax = adjusted.adminFeeTax;
  }

  const total = totalFromRoundedNetPerLine;

  return {
    subTotal: roundedSubTotal.toNumber(),
    tax: roundedTax.toNumber(),
    adminFee: roundedAdminFee.toNumber(),
    adminFeeTax: roundedAdminFeeTax.toNumber(),
    total: total.toNumber(),
  };
}
